import { x } from '@xstyled/styled-components';
import React, { useEffect, useLayoutEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { t } from 'ttag';
import { select } from '~/lib/select';
import { usePhoneAudioEffect } from '~/ui/audio-effect/usePhoneAudioEffect';
import { Flex } from '~/ui/flex/Flex';
import { MediaCaptions } from '~/ui/video-player/MediaCaptions';
import { useMediaPlayerStore } from '~/ui/video-player/MediaPlayerProvider';
import { MediaPlayerStore } from '~/ui/video-player/MediaPlayerStore';
import { shallow } from 'zustand/shallow';
import { useGlobalMedia } from '~/ui/video-player/useGlobalMedia';
import { TrainingState, useTrainingStore } from '~/training/state';
import { EditorMediaFile, useEditorStore } from '~/shell/Store';
import FilePickerV2 from '~/editor/filePicker/FilePicker_v2';
import useIsDarkMode from '~/lib/themes/hooks/useIsDarkMode';
import Avatar from './avatar.png';
import * as S from './styles';
import {
  getPhoneFitScale,
  getPhoneFrameSize,
  isPhoneMobileViewport,
  PHONE_FRAME_HEIGHT_PX,
  PHONE_FRAME_WIDTH_PX,
  PHONE_MOBILE_DESIGN_SCALE
} from './phoneReflowLayout';

const selectorTraining = (s: TrainingState) => select(s, 'logoUrl');
const selector = (s: MediaPlayerStore) =>
  select(s, 'pause', 'play', 'status', 'reset', 'loadMedia');

interface Props {
  id?: string;
  children: React.ReactNode;
  narration: string;
  subtitlesUrl?: string;
  autoPlay: boolean;
  name?: string;
  photo?: string;
  personalization?: boolean;
}

function getPhoneLayout(viewportWidth: number) {
  const frame = getPhoneFrameSize(viewportWidth);
  const isMobile = isPhoneMobileViewport(viewportWidth);
  const designScale = isMobile ? PHONE_MOBILE_DESIGN_SCALE : 1;
  const fitScale = getPhoneFitScale(viewportWidth, frame.width);
  const phoneTransformScale = designScale * fitScale;
  return {
    // Layout matches the visual size after transform (avoids clip / scroll).
    layoutWidth: PHONE_FRAME_WIDTH_PX * phoneTransformScale,
    layoutHeight: PHONE_FRAME_HEIGHT_PX * phoneTransformScale,
    /** Scale applied to the desktop-sized Phone node (design × WCAG fit). */
    phoneTransformScale,
    /** Keep tooltip at desktop visual size on mobile. */
    tooltipCounterScale: isMobile ? 1 / PHONE_MOBILE_DESIGN_SCALE : 1
  };
}

export const PhoneCall = ({
  id,
  children,
  narration,
  subtitlesUrl,
  name,
  photo,
  personalization = false
}: Props) => {
  const avatarRef = useRef<HTMLDivElement | null>(null);
  const { logoUrl } = useTrainingStore(selectorTraining, shallow);
  const { contentEditable, editorMode, updateFile } = useEditorStore();
  const isDarkMode = useIsDarkMode();
  const [showFilePicker, setShowFilePicker] = useState(false);
  const [phoneLayout, setPhoneLayout] = useState(() =>
    getPhoneLayout(typeof window !== 'undefined' ? window.innerWidth : Infinity)
  );

  const [hasStarted, setHasStarted] = useState(false);
  const startTimeoutRef = useRef<number | null>(null);
  const audioEffectPlayedRef = useRef<boolean>(false);
  const { play, pause, status, loadMedia } = useMediaPlayerStore(
    selector,
    shallow
  );

  const {
    audioEffectDuration,
    phoneAudioEffectIsPaused,
    pausePhoneAudioEffect
  } = usePhoneAudioEffect(editorMode);

  useGlobalMedia();

  // Desktop 248×503 (Figma 94:2680); mobile 205×414 (94:2681) + WCAG reflow.
  useLayoutEffect(() => {
    const updateLayout = () =>
      setPhoneLayout(getPhoneLayout(window.innerWidth));
    updateLayout();
    window.addEventListener('resize', updateLayout);
    return () => window.removeEventListener('resize', updateLayout);
  }, []);

  useEffect(() => {
    pause();
  }, []);

  useEffect(() => {
    // Use setTimeout(0) to avoid audio loading issues during slide transitions.
    // This pushes loadMedia to the next event loop cycle, allowing React cleanup
    // from previous slides to complete before loading new media
    setTimeout(() => {
      loadMedia(narration, subtitlesUrl, 'audio', true, 0);
    }, 0);
  }, [narration, subtitlesUrl]);

  useEffect(() => {
    if (hasStarted) {
      pausePhoneAudioEffect();
    }
  }, [hasStarted]);

  useEffect(() => {
    // Pause only if playing started automatically (autoplay), not by user click
    if (status === 'playing' && !audioEffectPlayedRef.current && !hasStarted) {
      audioEffectPlayedRef.current = true;
      pause();
    }

    if (
      startTimeoutRef.current === null &&
      !phoneAudioEffectIsPaused &&
      !hasStarted &&
      status === 'paused'
    ) {
      startTimeoutRef.current = window.setTimeout(() => {
        setHasStarted(true);
        play();
      }, audioEffectDuration);
    }

    return () => {
      if (status === 'paused') {
        setHasStarted(true);
      }

      if (startTimeoutRef.current !== null) {
        clearTimeout(startTimeoutRef.current as number);
        startTimeoutRef.current = null;
      }
    };
  }, [status]);

  const UpdateFileElement = async (file: EditorMediaFile) => {
    if (id) {
      updateFile(id, file, 'PHONE');
    }
    setShowFilePicker(false);
  };

  return (
    <Flex h="100%" alignItems="center" justifyContent="center">
      {/*
        Layout box matches the *visual* size after scale — transform alone would
        leave a fixed flow width and still force horizontal scroll.
      */}
      <x.div
        position="relative"
        style={{
          width: phoneLayout.layoutWidth,
          height: phoneLayout.layoutHeight
        }}
      >
        <S.Phone
          style={
            phoneLayout.phoneTransformScale < 1
              ? {
                  width: PHONE_FRAME_WIDTH_PX,
                  height: PHONE_FRAME_HEIGHT_PX,
                  transform: `scale(${phoneLayout.phoneTransformScale})`,
                  transformOrigin: 'top left'
                }
              : undefined
          }
        >
          <S.Contact>
            <div
              ref={avatarRef}
              style={{ display: 'inline-block', position: 'relative' }}
            >
              {photo || (personalization && logoUrl) ? (
                <x.img
                  src={photo || logoUrl}
                  w="32px"
                  h="32px"
                  borderRadius="50px"
                  backgroundColor="#FFF"
                  alt={''}
                  onClick={
                    contentEditable ? () => setShowFilePicker(true) : undefined
                  }
                  style={
                    contentEditable
                      ? { cursor: 'pointer', objectFit: 'contain' }
                      : { objectFit: 'contain' }
                  }
                />
              ) : (
                <x.img
                  src={Avatar}
                  w="32px"
                  h="32px"
                  borderRadius="50px"
                  backgroundColor="#FFF"
                  alt={''}
                  onClick={
                    contentEditable ? () => setShowFilePicker(true) : undefined
                  }
                  style={contentEditable ? { cursor: 'pointer' } : undefined}
                />
              )}

              {showFilePicker &&
                id &&
                createPortal(
                  <FilePickerV2
                    fileElRef={avatarRef}
                    updateFile={UpdateFileElement}
                    type="image"
                    onClose={() => setShowFilePicker(false)}
                  />,
                  document.body
                )}
            </div>

            <S.UserName>
              {(children as Array<Array<{ props?: { value?: unknown } }>>)[0][0]
                .props?.value
                ? children
                : name}
            </S.UserName>
          </S.Contact>

          <S.DeclineButton aria-hidden="true" />

          {/* `aria-labelledby` expects element ids (not text) and `tabindex > 0`
              breaks the natural tab order, so the button relies on its
              `aria-label` + `title` and the default button focusability. */}
          <S.CallButtonWrap>
            {!hasStarted && !editorMode && (
              <S.AcceptCallTooltip
                $isDark={isDarkMode}
                $counterScale={phoneLayout.tooltipCounterScale}
                role="tooltip"
              >
                {t`Click to answer`}
              </S.AcceptCallTooltip>
            )}
            <S.CallButton
              aria-label={t`Accept`}
              title={t`Click to answer`}
              onClick={() => {
                if (!hasStarted) {
                  audioEffectPlayedRef.current = true; // Prevent pause() in useEffect
                  play();
                  setHasStarted(true);
                }
              }}
            />
          </S.CallButtonWrap>
        </S.Phone>
      </x.div>

      <MediaCaptions />
    </Flex>
  );
};
